import {
  Input,
  Text,
  Textarea,
  VStack,
  Flex,
  Box,
  Group,
  Heading,
  ProgressCircle,
  AbsoluteCenter,
  Fieldset,
  useDisclosure,
  Badge
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Timesheet as TimesheetEntry } from "src/api/Timesheet";
import { deLocaleOption, elapsedMinutes, formatIso8601, formatMinutes } from "src/data/DateTimeUtil";
import { format } from "date-fns";
import {
  chakraComponents,
  OptionBase,
  Select as ReactSelect
} from "chakra-react-select";
import { timesheetData } from "./TimesheetData";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field";
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogCloseTrigger, DialogFooter } from "@/components/ui/dialog";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import { LabelAndValueOption } from "@/data/LabelAndValueOption";
import SeparatorText from "@/components/SeparatorText";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import ApprovalRejectDialog from "@/components/ApprovalRejectDialog";
import { Alert } from "@/components/ui/alert";
import ApprovalDecisionDetails from "@/components/ApprovalDecisionDetails";
import { useI18n } from "@/i18n/I18nContext";

interface TimesheetDialogProps {
  projectNames: LabelAndValueOption[];
  isOpen: boolean;
  onClose: () => void;
  entry: TimesheetEntry;
  onSave: (data: TimesheetEntry) => Promise<void>;
  onDelete: (data: TimesheetEntry) => void | Promise<void>;
  onApprove: (data: TimesheetEntry) => void | Promise<void>;
  onReject: (data: TimesheetEntry, reason: string) => void | Promise<void>;
  canEdit: boolean;
  canCancel: boolean;
  canApprove: boolean;
  canReject: boolean;
  employeeName?: string;
  isDeciding?: boolean;
  decisionError?: string | null;
}

interface EntryOption extends OptionBase {
  label: string;
  value: number;
  detail?: string;
  color: string;
}

const options = timesheetData.map((e) => {
  let eo: EntryOption = {
    label: e.name,
    value: e.id,
    color: e.color,
    detail: e.detail
  }
  return eo;
});

const customItem = (item: EntryOption) => (
  <Flex align="center" justifyContent="flex-start">
    <Box w="1rem" h="1rem" borderRadius="50%" bg={item?.color} mr="2px" shadow='md' />
    <Text ml="1px">{item?.label}</Text>
    <Text ml="1px" fontSize='.8rem' color="gray.500">{item?.detail}</Text>
  </Flex>
);

const customOption = (props) => (
  <chakraComponents.Option label={props.data.name} {...props}>
    {customItem(props.data)}
  </chakraComponents.Option>
);

const TimesheetDialog = ({
  projectNames,
  isOpen,
  onClose,
  entry,
  onSave,
  onDelete,
  onApprove,
  onReject,
  canEdit,
  canCancel,
  canApprove,
  canReject,
  employeeName,
  isDeciding = false,
  decisionError,
}: TimesheetDialogProps) => {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<TimesheetEntry>({
    defaultValues: entry
  });
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { open: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const saveInFlight = useRef(false);

  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const duration: string = formatMinutes(elapsedMinutes(startTime, endTime));
  const isRejected = entry.id > 0 && ApprovalStatusUtil.isRejected(entry.status);
  const busy = isSubmitting || isDeciding;

  const validateTimes = (endTime: string) => {
    if (!startTime || !endTime) return true;

    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return endMinutes > startMinutes || 'Endzeit muss nach der Startzeit liegen';
  };

  useEffect(() => {
    reset(entry);
  }, [entry, reset, isOpen]);

  const onSubmit: SubmitHandler<TimesheetEntry> = async (data) => {
    if (!canEdit || saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      // update minutes
      data.minutes = elapsedMinutes(data.startTime, data.endTime);
      data.localDate = formatIso8601(data.localDate);
      await onSave(data);
      onClose();
    } catch {
      setError("root.save", {
        message: "Zeiterfassung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut."
      });
    } finally {
      saveInFlight.current = false;
    }
  };

  const handleClose = () => {
    if (!saveInFlight.current && !isDeciding) onClose();
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={() => handleClose()} size='md' placement='center' motionPreset='scale'>
      <DialogContent>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader display="flex" flexDirection="column" alignItems="stretch" gap={2}>
            {employeeName && <Text fontWeight="bold">{employeeName}</Text>}
            <Heading textAlign="left" size="md">{format(entry.localDate, 'EEEE d. MMMM', deLocaleOption)}</Heading>
            <Heading textAlign='center' size='md'>
              {!canEdit ? t("approvals.timesheetDetails") : entry.id > -1 ? 'Zeiterfassung Bearbeiten' : 'Neue Zeiterfassung'}
              {entry.id > 0 && (
                <Badge ml=".5rem" colorPalette={ApprovalStatusUtil.color(entry.status)}>{ApprovalStatusUtil.label(entry.status)}</Badge>
              )}
            </Heading>
            <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
              {canReject && (
                <Button type="button" disabled={busy} colorPalette="orange" variant="outline" onClick={onRejectOpen}>
                  {t("approvals.actions.reject")}
                </Button>
              )}
              {canApprove && (
                <Button type="button" disabled={busy} loading={isDeciding} colorPalette="green" onClick={() => onApprove(entry)}>
                  {t("approvals.actions.approve")}
                </Button>
              )}
            </Group>
            {decisionError && <Alert status="error" role="alert">{decisionError}</Alert>}
          </DialogHeader>
          <DialogCloseTrigger disabled={busy} />

          <Fieldset.Root disabled={!canEdit || busy}>
            <DialogBody>
              <VStack gap={4}>
              {errors.root?.save && (
                <Alert status="error" role="alert" w="full">
                  {errors.root.save.message}
                </Alert>
              )}
              <ApprovalDecisionDetails entry={entry} />
              {/* Time inputs in the same row */}
              <Flex gap={4} w="full">
                <Field label='Startzeit' required invalid={!!errors.startTime} errorText={errors.startTime?.message}>
                  <Input
                    type="time"
                    autoFocus={canEdit}
                    {...register("startTime", {
                      required: 'Die Startzeit ist erforderlich'
                    })}
                  />
                </Field>

                <Field label='Endzeit' required invalid={!!errors.endTime} errorText={errors.endTime?.message}>
                  <Input
                    type="time"
                    {...register("endTime", {
                      required: 'Die Endzeit ist erforderlich',
                      validate: validateTimes
                    })}
                  />
                </Field>

                {/* based on workmodel dailyMinutes and percentage, can calculate if have daily overtime or not */}
                <Field label='Stunden' alignItems='flex-end'>
                  <ProgressCircle.Root size='md' key='balance' value={100} colorPalette='green'>
                    <ProgressCircle.Circle css={{ "--thickness": "2px" }}>
                      <ProgressCircle.Track />
                      <ProgressCircle.Range />
                    </ProgressCircle.Circle>
                    <AbsoluteCenter>
                      <Text fontWeight='bold'>{duration}</Text>
                    </AbsoluteCenter>
                  </ProgressCircle.Root>
                </Field>
              </Flex>

              {/* timesheet data entries with colors */}
              <Controller control={control} name='entryId'
                render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { invalid } }) => (
                  <Field id="entryId" label='Eintrag' invalid={invalid} errorText={errors.entryId?.message}>
                    <ReactSelect
                      isSearchable={true}
                      isClearable={true}
                      isDisabled={!canEdit || busy}
                      ref={ref}
                      name={name}
                      onBlur={onBlur}
                      placeholder='Eintrag wählen'
                      components={{ Option: customOption }}
                      formatOptionLabel={(option: EntryOption) => customItem(option)}
                      options={options}
                      onChange={val => onChange(val?.value ?? null)}
                      value={options.find(c => c.value === value)}
                    />
                  </Field>
                )} />

              <SeparatorText text='Eintrag oder Projekt' />

              <ProjectSelectController control={control} projectNames={projectNames} name='projectId' label='Projekt' readOnly={!canEdit || busy} />


              <Field label='Anmerkungen' invalid={!!errors.comments} errorText={errors.comments?.message}>
                <Textarea
                  placeholder='Kommentar hinzufügen...'
                  {...register("comments")}
                />
              </Field>
              </VStack>
            </DialogBody>
          </Fieldset.Root>


          <DialogFooter display="flex" flexDirection="column" alignItems="stretch" gap={2}>
            <ConfirmDialog
              heading={`${entry.startTime} - ${entry.endTime} stornieren`}
              isOpen={isDeleteOpen}
              onClose={onDeleteClose}
              onConfirm={async (): Promise<void> => onDelete(entry)}
            />
            <ApprovalRejectDialog
              heading={`${entry.startTime} - ${entry.endTime} ablehnen`}
              isOpen={isRejectOpen}
              onClose={onRejectClose}
              onReject={async (reason: string): Promise<void> => onReject(entry, reason)}
            />
            <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
              {canCancel && (
                <Button type="button" disabled={busy} colorPalette="red" me="auto" onClick={onDeleteOpen}>
                  Stornieren
                </Button>
              )}
              <CancelButton disabled={busy} onClick={handleClose} />
              {canEdit && <SubmitButton isSubmitting={isSubmitting} caption={isRejected ? "Erneut einreichen" : undefined} />}
            </Group>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default TimesheetDialog;
